import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Sparkles, Download, Edit3, Zap, Eye, EyeOff, Gift, Percent } from "lucide-react";
import { ShareButtons } from "@/components/ShareButtons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = ({ onLogin }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activePromo, setActivePromo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  useEffect(() => {
    loadActivePromo();
  }, []);

  const loadActivePromo = async () => {
    try {
      // Short timeout - don't let promo check block the page
      const response = await axios.get(`${API}/promos/active`, { timeout: 5000 });
      if (response.data.has_promo) {
        setActivePromo(response.data);
      }
    } catch (error) {
      // Silently fail - promos are optional
      console.log("Promo check skipped:", error.message);
    }
  };

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 7;
    const hasCapital = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~;']/.test(password);
    return { isValid: hasMinLength && hasCapital && hasSpecialChar, hasMinLength, hasCapital, hasSpecialChar };
  };

  const handleSubmit = async (mode) => {
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (mode === "register" && !formData.name) {
      toast.error("Please enter your name");
      return;
    }

    if (mode === "register") {
      const { isValid, hasMinLength, hasCapital, hasSpecialChar } = validatePassword(formData.password);
      if (!isValid) {
        let errorMsg = "Password must contain: ";
        const missing = [];
        if (!hasMinLength) missing.push("at least 7 characters");
        if (!hasCapital) missing.push("1 capital letter");
        if (!hasSpecialChar) missing.push("1 special character");
        toast.error(errorMsg + missing.join(", "));
        return;
      }
    }

    setLoading(true);

    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" 
        ? { name: formData.name, email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password };

      const response = await axios.post(`${API}${endpoint}`, payload);
      onLogin(response.data.token, response.data.user);
      setShowAuth(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
      {/* Promo Banner - Fixed at top */}
      {activePromo && activePromo.promo_type === "bogo" && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center">
            <div className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              <span className="font-bold text-lg">BUY ONE GET ONE FREE!</span>
            </div>
            <span className="text-green-100">Purchase any credit package and get DOUBLE the credits!</span>
            {activePromo.end_date ? (
              <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                <Zap className="h-4 w-4" />
                {(() => {
                  const endDate = new Date(activePromo.end_date);
                  const now = new Date();
                  const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                  if (daysLeft <= 0) return "Last Day!";
                  if (daysLeft === 1) return "Ends Tomorrow!";
                  if (daysLeft <= 7) return `${daysLeft} days left!`;
                  return `Ends ${endDate.toLocaleDateString()}`;
                })()}
              </div>
            ) : (
              <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
                Limited Time Offer
              </div>
            )}
          </div>
        </div>
      )}
      
      {activePromo && activePromo.promo_type === "percentage" && (
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 px-4">
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center">
            <div className="flex items-center gap-2">
              <Percent className="h-5 w-5" />
              <span className="font-bold text-lg">{activePromo.discount_percent}% OFF - {activePromo.name}!</span>
            </div>
            <span className="text-purple-100">Limited time discount on all credit packages</span>
            {activePromo.end_date && (
              <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                <Zap className="h-4 w-4" />
                {(() => {
                  const endDate = new Date(activePromo.end_date);
                  const now = new Date();
                  const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                  if (daysLeft <= 0) return "Last Day!";
                  if (daysLeft === 1) return "Ends Tomorrow!";
                  if (daysLeft <= 7) return `${daysLeft} days left!`;
                  return `Ends ${endDate.toLocaleDateString()}`;
                })()}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8">
        {/* Navigation */}
        <nav className="flex justify-between items-center mb-16 py-4">
          <div className="flex flex-col items-start">
            <span className="text-9xl font-bold playfair text-stone-900 tracking-tight">DTR</span>
            <span className="text-3xl font-bold text-stone-600 tracking-wide -mt-2 ml-2">Dream To Reality</span>
          </div>
          <Button 
            data-testid="nav-login-btn"
            onClick={() => setShowAuth(true)}
            className="bg-amber-900 hover:bg-amber-800 text-white px-12 py-4 rounded-lg font-semibold text-xl"
          >
            Sign In
          </Button>
        </nav>

        {/* Hero Content */}
        <div className="max-w-6xl mx-auto -mt-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            {/* Left Column - Text */}
            <div className="text-left space-y-6">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold playfair text-stone-900 leading-tight">
                Write Entire Books
                <br />
                <span className="text-amber-800">With AI</span>
              </h1>
              <p className="text-xl text-stone-600 leading-relaxed">
                Transform your ideas into complete books. Create multi-book series with perfect continuity. 
                Bring your stories to life with professional screenplay formatting. Powered by advanced AI.
              </p>
              <div className="flex gap-4 pt-4">
                <Button 
                  data-testid="hero-get-started-btn"
                  onClick={() => setShowAuth(true)}
                  size="lg"
                  className="bg-amber-900 hover:bg-amber-800 text-white px-8 py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Start your Writing Journey
                  <Sparkles className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Right Column - Visual: Book to Film Reel Transformation */}
            <div className="relative">
              <div className="flex items-center justify-center gap-4">
                {/* Book */}
                <div className="relative">
                  <svg width="140" height="180" viewBox="0 0 140 180" className="drop-shadow-xl">
                    {/* Book spine */}
                    <rect x="10" y="10" width="20" height="160" rx="2" fill="#78350f" />
                    {/* Book cover */}
                    <rect x="30" y="10" width="100" height="160" rx="2" fill="#92400e" />
                    {/* Book pages */}
                    <rect x="35" y="15" width="90" height="150" rx="1" fill="#fef3c7" />
                    {/* Text lines on page */}
                    <rect x="45" y="35" width="60" height="4" rx="1" fill="#d6d3d1" />
                    <rect x="45" y="47" width="70" height="3" rx="1" fill="#e7e5e4" />
                    <rect x="45" y="57" width="55" height="3" rx="1" fill="#e7e5e4" />
                    <rect x="45" y="67" width="65" height="3" rx="1" fill="#e7e5e4" />
                    <rect x="45" y="85" width="50" height="4" rx="1" fill="#d6d3d1" />
                    <rect x="45" y="97" width="70" height="3" rx="1" fill="#e7e5e4" />
                    <rect x="45" y="107" width="60" height="3" rx="1" fill="#e7e5e4" />
                    <rect x="45" y="117" width="68" height="3" rx="1" fill="#e7e5e4" />
                    <rect x="45" y="135" width="45" height="4" rx="1" fill="#d6d3d1" />
                    <rect x="45" y="147" width="65" height="3" rx="1" fill="#e7e5e4" />
                  </svg>
                </div>

                {/* Transformation Arrow */}
                <div className="flex flex-col items-center gap-2">
                  <svg width="80" height="40" viewBox="0 0 80 40" className="text-amber-600">
                    <defs>
                      <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#92400e" />
                        <stop offset="100%" stopColor="#d97706" />
                      </linearGradient>
                    </defs>
                    <path d="M5 20 L55 20 M45 10 L58 20 L45 30" stroke="url(#arrowGradient)" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-xs font-semibold text-amber-700 tracking-wide">AI MAGIC</span>
                </div>

                {/* Film Reel */}
                <div className="relative">
                  <svg width="160" height="180" viewBox="0 0 160 180" className="drop-shadow-xl">
                    {/* Film reel outer circle */}
                    <circle cx="80" cy="90" r="70" fill="#1c1917" />
                    <circle cx="80" cy="90" r="60" fill="#292524" />
                    {/* Center hole */}
                    <circle cx="80" cy="90" r="15" fill="#78350f" />
                    <circle cx="80" cy="90" r="10" fill="#1c1917" />
                    {/* Sprocket holes around the reel */}
                    <circle cx="80" cy="35" r="8" fill="#44403c" />
                    <circle cx="80" cy="145" r="8" fill="#44403c" />
                    <circle cx="35" cy="90" r="8" fill="#44403c" />
                    <circle cx="125" cy="90" r="8" fill="#44403c" />
                    <circle cx="48" cy="52" r="8" fill="#44403c" />
                    <circle cx="112" cy="52" r="8" fill="#44403c" />
                    <circle cx="48" cy="128" r="8" fill="#44403c" />
                    <circle cx="112" cy="128" r="8" fill="#44403c" />
                    {/* Film strip coming out */}
                    <rect x="130" y="70" width="25" height="40" rx="2" fill="#292524" />
                    <rect x="133" y="75" width="5" height="6" rx="1" fill="#44403c" />
                    <rect x="133" y="85" width="5" height="6" rx="1" fill="#44403c" />
                    <rect x="133" y="95" width="5" height="6" rx="1" fill="#44403c" />
                    {/* Film frames */}
                    <rect x="141" y="74" width="12" height="9" rx="1" fill="#fef3c7" />
                    <rect x="141" y="86" width="12" height="9" rx="1" fill="#fde68a" />
                    <rect x="141" y="98" width="12" height="9" rx="1" fill="#fef3c7" />
                  </svg>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-stone-300 rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="max-w-6xl mx-auto mt-32">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold playfair text-stone-900 mb-4">Everything You Need to Write</h2>
            <p className="text-xl text-stone-600">Professional tools for serious authors</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all p-8 border border-stone-100" data-testid="feature-ai-generation">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="h-8 w-8 text-amber-900" />
              </div>
              <h3 className="text-2xl font-bold playfair text-stone-900 mb-4">AI-Powered Writing</h3>
              <p className="text-stone-600 leading-relaxed">
                Generate complete books with GPT-5.1. Create detailed outlines and full chapters with professional ghostwriting quality.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all p-8 border border-stone-100" data-testid="feature-smart-editing">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl flex items-center justify-center mb-6">
                <BookOpen className="h-8 w-8 text-amber-900" />
              </div>
              <h3 className="text-2xl font-bold playfair text-stone-900 mb-4">Multi-Book Series</h3>
              <p className="text-stone-600 leading-relaxed">
                Create book series with perfect continuity. AI maintains characters, plot points, and tone across multiple books.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all p-8 border border-stone-100" data-testid="feature-export">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl flex items-center justify-center mb-6">
                <Edit3 className="h-8 w-8 text-amber-900" />
              </div>
              <h3 className="text-2xl font-bold playfair text-stone-900 mb-4">Book to Screenplay</h3>
              <p className="text-stone-600 leading-relaxed">
                Transform your novels into professional movie scripts. Standard screenplay formatting with scenes, dialogue, and directions.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto mt-32 mb-16">
          <div className="bg-gradient-to-br from-amber-900 via-amber-800 to-stone-800 rounded-3xl p-16 shadow-2xl text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-700 rounded-full opacity-10 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-stone-700 rounded-full opacity-10 blur-3xl"></div>
            
            <div className="relative z-10">
              <h2 className="text-4xl sm:text-5xl font-bold playfair text-white mb-6">
                Create a Movie Script Today
              </h2>
              <p className="text-amber-100 text-xl mb-10 max-w-2xl mx-auto">
                Join hundreds of authors and creators building worlds and adapting them into a full featured film with standard screenplay formatting.
              </p>
              <Button 
                data-testid="cta-get-started-btn"
                onClick={() => setShowAuth(true)}
                size="lg"
                className="bg-white text-amber-900 hover:bg-amber-50 px-10 py-6 text-xl rounded-xl shadow-lg hover:shadow-xl transition-all font-bold"
              >
                Create New Reality
                <Sparkles className="ml-2 h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <footer className="border-t border-stone-200 mt-16 py-12">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold playfair text-stone-900">DTR Creative</h3>
              <p className="text-stone-500 mt-1">Dream To Reality - AI Book Writing Platform</p>
            </div>
            
            <div className="flex flex-col items-center gap-3">
              <p className="text-stone-600 font-medium">Share with friends</p>
              <ShareButtons 
                url="https://dtrcreative.com"
                title="Check out DTR Creative - Transform your ideas into professionally written books with AI! 📚✨"
              />
            </div>
            
            <div className="text-center md:text-right text-stone-500 text-sm">
              <p>© {new Date().getFullYear()} DTR Creative</p>
              <p className="mt-1">All rights reserved</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={(open) => {
        setShowAuth(open);
        if (open) {
          // Clear form when dialog opens to prevent auto-fill issues
          setFormData({ name: "", email: "", password: "" });
        }
      }}>
        <DialogContent className="sm:max-w-lg" data-testid="auth-dialog" aria-describedby="auth-dialog-description">
          <DialogHeader className="space-y-4">
            <DialogTitle className="text-4xl playfair text-stone-900 text-center font-bold">
              DTR
              <span className="block text-3xl font-bold text-stone-600 tracking-wide mt-1 ml-2">Dream To Reality</span>
            </DialogTitle>
            <p className="text-xl text-stone-700 text-center font-semibold">Welcome Back</p>
            <p className="text-center text-stone-600">Start writing your book with AI</p>
            <p id="auth-dialog-description" className="sr-only">Login or register to start writing your book with AI</p>
          </DialogHeader>
          
          {/* Google Sign In Button - Available on both tabs */}
          <div className="space-y-4">
            <Button 
              data-testid="google-signin-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              variant="outline"
              className="w-full border-stone-300 hover:bg-stone-50 text-stone-700 font-medium py-5"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-stone-500">Or continue with email</span>
              </div>
            </div>
          </div>
          
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                data-testid="login-submit-btn"
                onClick={() => handleSubmit("login")}
                disabled={loading}
                className="w-full bg-amber-900 hover:bg-amber-800 text-white"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </TabsContent>
            
            <TabsContent value="register" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="register-name">Name</Label>
                <Input
                  id="register-name"
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-email">Email</Label>
                <Input
                  id="register-email"
                  data-testid="register-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-password">Password</Label>
                <div className="relative">
                  <Input
                    id="register-password"
                    data-testid="register-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-stone-500 mt-1">
                  Password must contain at least 7 characters, 1 capital letter, and 1 special character.
                </p>
              </div>
              <Button 
                data-testid="register-submit-btn"
                onClick={() => handleSubmit("register")}
                disabled={loading}
                className="w-full bg-amber-900 hover:bg-amber-800 text-white"
              >
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;